import type { ProgramResult } from "bear/revit/cloud-program";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  canCheckExecution,
  type Artifact,
  type Execution,
} from "playground/migrations/contract";
import { useCallback, useEffect, useState } from "react";

import { ChangeRow } from "@/components/TaskChanges";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function jigName(jig: string): string {
  switch (jig) {
    case "revit-cloud":
      return "Revit cloud model";
    case "simulator":
      return "Simulation";
    default:
      return jig;
  }
}

export function MigrationReview({
  taskId,
  artifact,
}: {
  taskId: string;
  artifact: Artifact;
}) {
  return (
    <ChangeRow
      icon={ArrowUpRight}
      title={artifact.title}
      description={jigName(artifact.jig)}
      badge={<Badge variant="outline">On approval</Badge>}
    >
      <ActionPreview taskId={taskId} digest={artifact.digest} />
      <MigrationDetails artifact={artifact} />
    </ChangeRow>
  );
}

type Preview =
  | { status: "submitting" | "submitted"; digest: string }
  | { status: "failed"; reason: string; digest: string }
  | { status: "complete"; result: ProgramResult; digest: string };

/** What the action would do, run without effect; approval requires it for Jigs that preview. */
function ActionPreview({ taskId, digest }: { taskId: string; digest: string }) {
  const [preview, setPreview] = useState<Preview | null | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const url = `/api/tasks/${taskId}/action/preview`;
  const load = useCallback(async () => {
    const res = await fetch(url);
    if (!res.ok) return setError(await res.text());
    setPreview(await res.json());
  }, [url]);
  useEffect(() => void load(), [load, digest]);
  const pending =
    preview?.status === "submitting" || preview?.status === "submitted";
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => void load(), 10_000);
    return () => clearInterval(timer);
  }, [pending, load]);
  const start = async () => {
    setStarting(true);
    setError(null);
    const res = await fetch(url, { method: "POST" });
    setStarting(false);
    if (!res.ok) return setError(await res.text());
    setPreview(await res.json());
  };
  if (preview === undefined && !error) return null;
  const result = preview?.status === "complete" ? preview.result : null;
  return (
    <div className="mb-4 flex min-w-0 flex-col gap-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="font-medium">Preview</span>
        <Button
          size="sm"
          variant="outline"
          disabled={starting || pending}
          aria-busy={starting || pending}
          onClick={() => void start()}
        >
          {(starting || pending) && (
            <Loader2
              data-icon="inline-start"
              className="animate-spin [animation-duration:3s] motion-reduce:animate-none"
              aria-hidden
            />
          )}
          {pending
            ? "Running in Revit…"
            : preview
              ? "Preview again"
              : "Preview in Revit"}
        </Button>
      </div>
      {!preview && !pending && (
        <p className="text-muted-foreground">
          Approval runs only a previewed action. Preview runs the program in
          Revit and rolls it back.
        </p>
      )}
      {error && <p className="text-destructive">{error}</p>}
      {preview?.status === "failed" && (
        <p className="text-destructive">
          The preview job ended with {preview.reason}.
        </p>
      )}
      {result?.status === "rejected" && (
        <p className="text-destructive">{result.message}</p>
      )}
      {result && result.status !== "rejected" && (
        <PreviewChanges result={result} />
      )}
    </div>
  );
}

function PreviewChanges({
  result,
}: {
  result: Exclude<ProgramResult, { status: "rejected" }>;
}) {
  const { changes } = result;
  const groups = (elements: typeof changes.changed) =>
    Object.entries(
      Object.groupBy(elements, (element) => element.category ?? "Other"),
    ).map(([category, items]) => `${items?.length ?? 0} ${category}`);
  return (
    <div className="flex min-w-0 flex-col gap-1 text-muted-foreground">
      <p>
        Revit would change {changes.changed.length} of {changes.elements}{" "}
        elements: {groups(changes.changed).join(", ") || "none"}.
      </p>
      <p>
        Added {changes.added.length}, removed {changes.removed.length}. Joins
        broken: {changes.joinsBroken.length}. Revit warnings:{" "}
        {result.warnings.length}.
      </p>
      {result.warnings.map((warning) => (
        <p key={warning} className="text-amber-700 dark:text-amber-400">
          {warning}
        </p>
      ))}
      <details>
        <summary className="cursor-pointer">
          Elements and program output
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
          {JSON.stringify(
            { output: result.output, changes, revision: result.revision },
            null,
            2,
          )}
        </pre>
      </details>
    </div>
  );
}

function MigrationDetails({ artifact }: { artifact: Artifact }) {
  return (
    <div className="flex min-w-0 flex-col gap-4 text-sm">
      <p className="text-muted-foreground">
        This action runs after the Bear changes are applied. If it fails, the
        Bear changes remain applied.
      </p>
      <dl className="flex min-w-0 flex-col gap-1">
        <dt className="font-medium">Destination</dt>
        <dd className="break-all font-mono text-xs text-muted-foreground">
          {artifact.target}
        </dd>
      </dl>
      <details>
        <summary className="cursor-pointer font-medium">
          Input and script
        </summary>
        <div className="mt-3 flex min-w-0 flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            Approval uses this exact input and script version.
          </p>
          <pre className="max-h-64 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
            {JSON.stringify(artifact.input, null, 2)}
          </pre>
          {Object.entries(artifact.sources).map(([path, source]) => (
            <div key={path} className="flex min-w-0 flex-col gap-2">
              <p className="break-all font-mono text-xs text-muted-foreground">
                {path}
              </p>
              <pre className="max-h-64 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
                {source}
              </pre>
            </div>
          ))}
          <p className="break-all text-xs text-muted-foreground">
            Content fingerprint: {artifact.digest}
          </p>
        </div>
      </details>
    </div>
  );
}

type ResultCheck =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "checked"; at: string }
  | { status: "failed"; message: string };

export function MigrationStatus({
  execution,
  run,
  reconcile,
}: {
  execution: Execution;
  run: () => void;
  reconcile: () => Promise<void>;
}) {
  const [check, setCheck] = useState<ResultCheck>({ status: "idle" });
  const state = execution.state;
  const busy = state.status === "running" || state.status === "submitted";
  const checkResult = async () => {
    if (check.status === "checking") return;
    setCheck({ status: "checking" });
    try {
      await reconcile();
      setCheck({ status: "checked", at: new Date().toISOString() });
    } catch (error) {
      setCheck({
        status: "failed",
        message:
          error instanceof Error ? error.message : "Could not check the result",
      });
    }
  };
  return (
    <Alert role="status" aria-busy={busy}>
      <AlertTitle className="flex items-center gap-2">
        Bear changes applied
        <Badge variant="outline">{jigName(execution.artifact.jig)}</Badge>
      </AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-3">
        <p className="flex items-start gap-2 font-medium">
          {busy && (
            <Loader2
              className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground [animation-duration:3s] motion-reduce:animate-none"
              aria-hidden
            />
          )}
          {execution.artifact.title}
        </p>
        {state.status === "pending" && (
          <>
            <p>The approved external action has not started.</p>
            <Button size="sm" variant="outline" onClick={run}>
              Run approved action
            </Button>
          </>
        )}
        {state.status === "running" && (
          <p>
            The external action started, but its result is not yet confirmed.
          </p>
        )}
        {state.status === "succeeded" && (
          <p>
            External action completed and verified.
            {state.receipts.length === 0
              ? " No external writes were made."
              : ""}
          </p>
        )}
        {state.status === "submitted" && (
          <p>The external service is processing the approved action.</p>
        )}
        {state.status === "submitting" && (
          <p>
            Submission has not been confirmed. Check again for an
            acknowledgement; this action will not submit again.
          </p>
        )}
        {canCheckExecution(state) && (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={check.status === "checking"}
              aria-busy={check.status === "checking"}
              onClick={() => void checkResult()}
            >
              {check.status === "checking" && (
                <Loader2
                  data-icon="inline-start"
                  className="animate-spin [animation-duration:3s] motion-reduce:animate-none"
                  aria-hidden
                />
              )}
              {check.status === "checking"
                ? "Checking…"
                : state.status === "running"
                  ? "Stop and check result"
                  : "Check external result"}
            </Button>
            {check.status === "checked" && (
              <p role="status" className="text-xs text-muted-foreground">
                Checked at{" "}
                <time dateTime={check.at}>
                  {new Date(check.at).toLocaleTimeString()}
                </time>
                .
              </p>
            )}
            {check.status === "failed" && (
              <p role="alert" className="text-destructive">
                Could not check the result: {check.message}
              </p>
            )}
          </>
        )}
        {state.status === "differed" && (
          <p className="text-amber-700 dark:text-amber-400">
            Done in the external system, but different from the preview you
            approved. {state.message}
          </p>
        )}
        {(state.status === "failed" || state.status === "unknown") && (
          <p className="text-destructive">
            {state.status === "failed"
              ? "External action failed."
              : "External result is uncertain."}{" "}
            {state.message} Inspect the recorded result before proposing a new
            task.
          </p>
        )}
        {"receipts" in state && state.receipts.length > 0 && (
          <details className="w-full min-w-0">
            <summary className="cursor-pointer">Result details</summary>
            <pre className="max-h-48 overflow-auto text-xs">
              {JSON.stringify(state.receipts, null, 2)}
            </pre>
          </details>
        )}
        <details className="w-full min-w-0">
          <summary className="cursor-pointer">Approved action details</summary>
          <div className="mt-3">
            <MigrationDetails artifact={execution.artifact} />
          </div>
        </details>
      </AlertDescription>
    </Alert>
  );
}
